import React, { useState, useEffect, useMemo } from "react";
import { Form } from "react-formio";
import "./formRender.css";
import { getContentId, getJsonFromObjectStore } from "../networkservice/FormService";
import BackButton from "../../../../experience/generic/elements/backbutton/BackButton";
import { useReadMemory } from "../../../../experience/generic/hooks/useReadMemory";
import MemoryType from "../../../../service/memory/enum/MemoryType";
import { executeIntent } from "../../../../experience/utils/IntentExecutor";
import IntentType from "../../../../intentexecutor/utils/IntentType";
import { useRecoilValue } from "recoil";
import { currentConfiguration } from "../../../../service/memory/recoil/configurationstore";
import ConfigurationConstants from "../../../../experience/utils/configurationconstants";
import { useGetRole } from "../../../../experience/generic/hooks/useGetRole";
import ErrorNotificationComponent from "../../error/ErrorNotificationComponent";
import Role from "../../enums/Role";
import { Button, Popconfirm, Space, Spin, TimePicker, Tooltip, Typography } from "antd";
import { CheckCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { mapIntentToRequest } from "../../../../service/network/RequestMapper";
import { performPostRequest } from "../../../../service/network/NetworkService";
import { getApiUrl } from "../../../../service/network/UrlMapper";
import { useGetOrgIdentifier } from "../../../../experience/generic/hooks/useGetOrgIdentifier";
import CalenderAndDate from "../../../../experience/carecoordinator/requestforvolunteeringservice/components/CalenderAndDate";
import {
  buildVisitTimestamp,
  convertDateToSODTimestamp,
  dateAndTime,
  getDateString,
  getTimeSpentFromTimestamps,
  getTimeString,
  getVisitTimeValidationError,
  normalizeVisitEndTimestamp,
} from "../../../../utils/dateutils";
import moment from "moment";
import { getFullName } from "../../../../experience/careprovider/visits/utils";
import SelectedProfileCard from "../../cards/SelectedProfileCard";
import FormActionsButtons from "./components/FormActionsButtons";
import useGetWorkforceDetailsByIdentifier from "../../../../experience/generic/hooks/useGetWorkforceDetailsByIdentifier";
import SelectWorkforce from "../../../../experience/carecoordinator/workforce/SelectWorkforce";
import { coordinatorDetails } from "../../../recoil/coordinatorRecoil";
import { useNavigate } from "react-router";
import { RoleToLinkMapper } from "../../../../experience/utils/UrlUtils";
import apiService from "../../../../service/apiservice/apiService";
import { strings } from "../../../../experience/utils/strings";
import SmartIcon from "../../genericIcon/SmartIcon";
import {
  buildReportFileName,
  renderFormReportPdf,
} from "./formReportPdf";

const { Text } = Typography;

const FormRenderer = ({
  templateCode,
  patientIdentifier,
  formSubmitCallback,
  workforceIdentifier,
  formIdentifier,
  eventType,
  isFormRenderedOnModal,
  formComments,
  formApproveComments,
  dataAnalyzerCallback,
  setOpenAnayzerModal,
  readOnly,
  formSummary,
  additionalFormDetails,
  setAdditionalFormDetails,
  opportunities,
  taskId,
  isCoordinatorForm,
  className,
  isApproveCommentsAllowed
}) => {
  const [error, setError] = useState(undefined);
  const [formReadOnly, setFormReadOnly] = useState(readOnly);
  const [formTemplateData, setFormTemplateData] = useState(null);
  const [formName, setFormName] = useState("");
  const [isCountTowards5percent, setisCountTowards5percent] = useState(false);
  const [formAttributes, setFormAttributes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filledFormData, setFilledFormData] = useState({});
  const [isDataAnalyzerAllowed, setIsDataAnalyzerAllowed] = useState(false);
  const configuration = useRecoilValue(currentConfiguration);
  const secretKey =
    configuration[ConfigurationConstants.NIRAMAYA_ORGANIZATION_SECRETKEY];
  const formBucketName =
    configuration[ConfigurationConstants.NIRAMAYA_FORMS_BUCKETNAME];
  const [workforceDetails] =
    useGetWorkforceDetailsByIdentifier(workforceIdentifier);
  const role = useGetRole();
  const userDetails = useReadMemory(MemoryType.USER);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const organizationIdentifier = useGetOrgIdentifier();
  const [patientName, setPatientName] = useState([]);
  const [workforceName, setWorkforceName] = useState([]);
  const coordinator = useRecoilValue(coordinatorDetails);
  const [patientDetails, setPatientDetails] = useState(undefined);
  const [startTime, setStartTime] = useState(
    additionalFormDetails?.startTime
      ? moment(additionalFormDetails.startTime).valueOf()
      : undefined
  );
  const [endTime, setEndTime] = useState(
    additionalFormDetails?.endTime
      ? moment(additionalFormDetails.endTime).valueOf()
      : undefined
  );
  const [taskDate, setTaskDate] = useState(
    additionalFormDetails?.taskDate ? additionalFormDetails?.taskDate : ""
  );
  const navigate = useNavigate();
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Step 2: Get form data if formIdentifier is Present
  const getFormDataAfterLoading = async () => {
    try {
      const payload = {
        formIdentifier,
        templateCode,
        secretKey: secretKey?.key,
      };

      const savedFormData = await executeIntent(
        IntentType.GET_FORMDATA,
        payload
      );

      if (savedFormData[0]?.formData) {
        setFilledFormData(JSON.parse(savedFormData[0]?.formData));
      } else {
        setError({
          message: "No saved data was found for this form",
        });
      }
    } catch (err) {
      setError(err);
    }
  };

  const handleFormChange = (submission) => {
    setFilledFormData(submission.data);
  };

  // Step 3: Submit form on click of Submit Button
  const submitUserResponse = async (userResponse, status) => {
    setIsLoading(true);
    const payload = {
      data: {
        organizationIdentifier: userDetails?.organizationIdentifier,
        templateCode: templateCode,
        userIdentifier: userDetails?.userIdentifier,
        formData: userResponse.data,
        patientIdentifier: patientIdentifier,
        workforceIdentifier: workforceIdentifier,
        formName: formName,
        status: status,
        creatorIdentifier: userDetails?.userIdentifier,
      },
    };
    if (role === Role.VOLUNTEER_COORDINATOR) {
      payload.data = { ...payload.data, isFilledByCoordinator: true };
    }
    if (formIdentifier) {
      payload.data = { ...payload.data, formIdentifier: formIdentifier };
    }
    if (eventType) {
      payload.data = { ...payload.data, eventType: eventType };
    }
    if (patientDetails) {
      payload.data = {
        ...payload.data,
        givenName: patientDetails?.givenName,
        familyName: patientDetails?.familyName,
      };
    }
    const volunteerId =
      !Array.isArray(workforceName) &&
      workforceName?.value &&
      JSON.parse(workforceName?.value).userIdentifier;

    const formDataResponse = await executeIntent(
      IntentType.ADD_FORMDATA,
      payload
    );
    setIsLoading(false);
    formSubmitCallback(formDataResponse, status, volunteerId);
  };

  const handleSaveClick = () => {
    submitUserResponse({ data: filledFormData }, "Filled");
  };

  const handleDeleteClick = async () => {
    setDeleteLoading(true);
    const endpoint = "niramayafoundation/niramayavisits/api/v1/task";
    const param = {
      organizationIdentifier,
      id: taskId,
    };
    const response = await apiService.delete(
      endpoint,
      param,
      strings.VISIT_DELETE_SUCCESS,
      strings.VISIT_DELETE_ERROR
    );
    setDeleteLoading(false);
    if (response?.message) {
      navigate(`${RoleToLinkMapper(role)}homepage`);
    }
  };

  const handleAnalyzeFormData = async () => {
    setOpenAnayzerModal(true);
    setIsDataLoading(true);
    const workforceData = JSON.parse(workforceName.value || "{}");
    const additionalFormData = {
      startTime: getTimeString(additionalFormDetails?.startTime),
      endTime: getTimeString(additionalFormDetails?.endTime),
      taskDate: getDateString(additionalFormDetails?.taskDate),
      patientName: getFullName(
        patientDetails.givenName,
        patientDetails.familyName
      ),
      workforceName: getFullName(
        workforceData.givenName,
        workforceData.familyName
      ),
    };

    const payload = {
      text:
        filledFormData &&
        JSON.stringify({ ...filledFormData, ...additionalFormData }),
      engine: "FormSummarizer",
    };

    const request = mapIntentToRequest(IntentType.ANALYZE_DATA, payload);
    let result = await performPostRequest(
      await getApiUrl()[IntentType.ANALYZE_DATA],
      JSON.stringify(request)
    );
    result = await result.json();
    setIsDataLoading(false);
    dataAnalyzerCallback(result, isDataLoading);
  };

  const onDateChange = (formattedDate) => {
    const newTaskDate = convertDateToSODTimestamp(formattedDate);

    setTaskDate(newTaskDate);

    setAdditionalFormDetails((prev) => {
      const updated = { ...prev, taskDate: newTaskDate };

      // Re-anchor the already selected times to the new service date, keeping an
      // overnight end time on the day after it.
      if (prev.startTime) {
        updated.startTime = buildVisitTimestamp(newTaskDate, prev.startTime);
      }

      if (prev.endTime) {
        updated.endTime = normalizeVisitEndTimestamp(
          updated.startTime,
          buildVisitTimestamp(newTaskDate, prev.endTime)
        );
      }

      return updated;
    });
  };

  const handleSetStartTime = (time) => {
    if (!time) {
      setStartTime(null);
      setAdditionalFormDetails((prev) => ({ ...prev, startTime: null }));
      return;
    }

    const startTimestamp = buildVisitTimestamp(taskDate, time.format());

    setStartTime(startTimestamp);
    setEndTime(startTimestamp);

    setAdditionalFormDetails((prev) => ({
      ...prev,
      startTime: startTimestamp,
      endTime: startTimestamp,
    }));
  };

  const handleSetEndTime = (time) => {
    if (!time) {
      setEndTime(null);
      setAdditionalFormDetails((prev) => ({ ...prev, endTime: null }));
      return;
    }

    const endTimestamp = normalizeVisitEndTimestamp(
      buildVisitTimestamp(taskDate, startTime),
      buildVisitTimestamp(taskDate, time.format())
    );

    setEndTime(endTimestamp);

    setAdditionalFormDetails((prev) => ({
      ...prev,
      endTime: endTimestamp,
    }));
  };

  const exportPDF = () => {
    const reportSubmittedBy = `${getFullName(
      coordinator?.givenName ?? workforceDetails?.givenName,
      coordinator?.familyName ?? workforceDetails?.familyName
    )} (${coordinator?.email ?? workforceDetails?.email}) (${
      coordinator?.jobTitle ?? workforceDetails?.jobTitle
    })`;

    const reportInfoRows = [
      { label: "Submitted By", value: reportSubmittedBy },
      {
        label: "Submitted On",
        value: additionalFormDetails?.createdTimestamp
          ? dateAndTime(additionalFormDetails?.createdTimestamp)
          : "--",
      },
    ];

    if (additionalFormDetails?.status) {
      const statusText =
        additionalFormDetails.status === "Submitted"
          ? "Pending Approval"
          : additionalFormDetails.status;
      reportInfoRows.push({ label: "Status", value: statusText });
    }

    if (
      !workforceIdentifier ||
      workforceIdentifier === "undefined" ||
      coordinator
    ) {
      const workforceData = JSON.parse(workforceName.value);
      reportInfoRows.push({
        label: "On the Volunteer's Behalf",
        value: `${getFullName(workforceData.givenName, workforceData.familyName)} (${workforceData.email})`,
      });
    }

    const patientVisitRows = [];

    if (patientDetails) {
      patientVisitRows.push({
        label: "Patient Name",
        value: `${getFullName(patientDetails.givenName, patientDetails.familyName)} (${patientDetails?.medicalRecordNumber})`,
      });
    }

    if (additionalFormDetails?.taskDate) {
      patientVisitRows.push({
        label: "Service Date",
        value: additionalFormDetails?.taskDate
          ? getDateString(additionalFormDetails.taskDate)
          : "--",
      });
    }

    if (additionalFormDetails?.startTime) {
      patientVisitRows.push({
        label: "Start Time",
        value: additionalFormDetails.startTime
          ? getTimeString(additionalFormDetails.startTime)
          : "--",
      });
    }

    if (additionalFormDetails?.endTime) {
      patientVisitRows.push({
        label: "End Time",
        value: additionalFormDetails.endTime
          ? getTimeString(additionalFormDetails.endTime)
          : "--",
      });
    }

    if (additionalFormDetails?.startTime && additionalFormDetails?.endTime) {
      patientVisitRows.push({
        label: "Time Spent",
        value:
          getTimeSpentFromTimestamps(
            additionalFormDetails?.startTime,
            additionalFormDetails?.endTime
          ) || "0h 0m",
      });
    }

    const doc = renderFormReportPdf({
      formName,
      reportInfoRows,
      patientVisitRows,
      formLabels: formTemplateData?.components,
      formValues: filledFormData,
    });

    const fileName = buildReportFileName({
      medicalRecordNumber: patientDetails?.medicalRecordNumber,
      formName,
      taskDate: additionalFormDetails?.taskDate,
      getDateString,
    });
    doc.save(fileName);
  };

  const isTimeRequired = formAttributes?.includes("Time Required");
  const isDateRequired = formAttributes?.includes("Date Required");
  const isVolunteerCoordinatorCondition =
    formAttributes?.some(
      (attr) => attr === "Volunteer Default" || attr === "VC/PFA"
    ) &&
    userDetails?.jobTitle === "Volunteer Coordinator" &&
    (!workforceIdentifier ||
      workforceIdentifier === "undefined" ||
      coordinator);

  const timeValidationError = useMemo(
    () =>
      isTimeRequired ? getVisitTimeValidationError(startTime, endTime) : null,
    [endTime, isTimeRequired, startTime]
  );

  const missingRequiredFields = useMemo(() => {
    const missing = [];

    if (isDateRequired && !taskDate) {
      missing.push("Date Service Performed");
    }
    if (isTimeRequired && !startTime) {
      missing.push("Start Time");
    }
    if (isTimeRequired && !endTime) {
      missing.push("End Time");
    }
    if (isVolunteerCoordinatorCondition && !workforceName?.value) {
      missing.push("On the Volunteer's Behalf");
    }

    return missing;
  }, [
    endTime,
    isDateRequired,
    isTimeRequired,
    isVolunteerCoordinatorCondition,
    startTime,
    taskDate,
    workforceName?.value,
  ]);

  const dynamicSchema = useMemo(() => {
    const updatedSchema = JSON.parse(JSON.stringify(formTemplateData || {}));

    // If formSummary is true → remove the submit component
    if (formSummary && Array.isArray(updatedSchema?.components)) {
      updatedSchema.components = updatedSchema?.components?.filter(
        (c) => c.action !== "submit",
      );
      return updatedSchema;
    }

    const submitComponent = updatedSchema?.components?.find(
      (c) => c.action === "submit",
    );

    if (submitComponent) {
      if (isTimeRequired && isDateRequired && isVolunteerCoordinatorCondition) {
        submitComponent.disabled = !(
          startTime &&
          endTime &&
          taskDate &&
          workforceName?.value
        );
      } else if (isTimeRequired && isDateRequired) {
        submitComponent.disabled = !(startTime && endTime && taskDate);
      } else if (isTimeRequired && isVolunteerCoordinatorCondition) {
        submitComponent.disabled = !(
          startTime &&
          endTime &&
          workforceName?.value
        );
      } else if (isDateRequired && isVolunteerCoordinatorCondition) {
        submitComponent.disabled = !(taskDate && workforceName?.value);
      } else if (isTimeRequired) {
        submitComponent.disabled = !(startTime && endTime);
      } else if (isDateRequired) {
        submitComponent.disabled = !taskDate;
      } else if (isVolunteerCoordinatorCondition) {
        submitComponent.disabled = !workforceName?.value;
      } else {
        submitComponent.disabled = false;
      }

      if (timeValidationError) {
        submitComponent.disabled = true;
      }
    }

    return updatedSchema;
  }, [
    endTime,
    formTemplateData,
    isDateRequired,
    isTimeRequired,
    isVolunteerCoordinatorCondition,
    startTime,
    taskDate,
    timeValidationError,
    workforceName?.value,
    formSummary,
  ]);
  
  useEffect(() => {
    const configurationDataAnalyzer =
      configuration[
        ConfigurationConstants.FEATUREACCESS_FORM_RENDERER_DATA_ANALYZER
      ];
    setIsDataAnalyzerAllowed(configurationDataAnalyzer?.visibility);
  }, [configuration]);

  useEffect(() => {
    const getPatientDetails = async () => {
      const payload = {
        userIdentifier: patientIdentifier,
        secretKey: secretKey?.key,
      };
      const userData = await executeIntent(
        IntentType.GET_PATIENT_DETAILS_BY_USER_IDENTIFIER,
        payload
      );
      setPatientDetails(userData[0]);
      setPatientName({
        label: getFullName(userData[0]?.givenName, userData[0]?.familyName),
        value: JSON.stringify(userData[0]),
        misc: userData[0]?.birthDate
          ? `MRN: ${userData[0]?.medicalRecordNumber} | DOB: ${moment(
              userData[0]?.birthDate
            )?.format("MMM D, YYYY")}`
          : `MRN: ${userData[0]?.medicalRecordNumber}`,
      });
    };

    if (secretKey && patientIdentifier && patientIdentifier !== "null") {
      getPatientDetails();
    }
  }, [patientIdentifier, secretKey]);

  useEffect(() => {
    setTaskDate(additionalFormDetails?.taskDate);
    setStartTime(additionalFormDetails?.startTime);
    setEndTime(additionalFormDetails?.endTime);
  }, [
    additionalFormDetails?.endTime,
    additionalFormDetails?.startTime,
    additionalFormDetails?.taskDate,
  ]);

  useEffect(() => {
    // Step 1: Load form using Template Code
    async function loadFormFromTemplateCode() {
      const contentIdObject = await getContentId(templateCode);
      const metadata = JSON.parse(contentIdObject?.data?.metadata || "{}");
      setisCountTowards5percent(contentIdObject?.data?.countTowards5percent);
      setFormAttributes(metadata?.attributes);

      if (contentIdObject.data != null) {
        setIsLoading(true);
        try {
          if (formBucketName?.value !== undefined) {
            const form = await getJsonFromObjectStore(
              contentIdObject.data.contentId,
              formBucketName?.value
            );
            setFormTemplateData(form);
            setFormName(contentIdObject.data.title);
            if (formIdentifier && formIdentifier !== "undefined") {
              await getFormDataAfterLoading();
            }
          }
        } catch (err) {
          setError(err);
        } finally {
          setIsLoading(false);
        }
      }
    }
    loadFormFromTemplateCode();
  }, [configuration]);

  useEffect(() => {
    if (workforceIdentifier && workforceIdentifier !== "undefined") {
      setWorkforceName({
        label: getFullName(
          workforceDetails?.givenName,
          workforceDetails?.familyName
        ),
        value: JSON.stringify(workforceDetails),
        misc: workforceDetails?.email && `Email: ${workforceDetails.email}`,
      });
    } else if (
      (!workforceIdentifier || workforceIdentifier === "undefined") &&
      opportunities?.length === 1
    ) {
      setWorkforceName({
        label: getFullName(
          opportunities[0]?.workforce?.givenName,
          opportunities[0]?.workforce?.familyName
        ),
        value: JSON.stringify(opportunities[0]?.workforce),
        misc:
          opportunities[0]?.workforce?.email &&
          `Email: ${opportunities[0]?.workforce?.email}`,
      });
    }
  }, [opportunities, workforceDetails, workforceIdentifier]);

  const isSaveDisabled =
    Object.keys(filledFormData).length === 0 ||
    formReadOnly ||
    !!startTime !== !!endTime ||
    Boolean(timeValidationError) ||
    (formAttributes?.some(
      (attribute) =>
        attribute === "Volunteer Default" || attribute === "VC/PFA"
    ) &&
      userDetails?.jobTitle === "Volunteer Coordinator" &&
      (!workforceIdentifier ||
        workforceIdentifier === "undefined" ||
        coordinator) &&
      !workforceName?.value);

  return (
    <>
      {error !== undefined && (
        <ErrorNotificationComponent
          errorDetails={{
            title: "Error!",
            message:
              "Something went wrong while loading the form.\n\n" +
              error.message,
            setErrorOnRender: setError,
          }}
        />
      )}
      <div className={`form-renderer ${className}`}>
        {isLoading ? (
          <Spin size="medium" className="spinnerCenter" />
        ) : (
          <>
            {formComments && (
              <div className="form-rejection-message-container">
                <Space align="start">
                  <Text className="rejection-icon">
                    <ExclamationCircleOutlined />
                  </Text>
                  <Text className="form-rejection-message">
                    Reason for edit request: {formComments}
                  </Text>
                </Space>
              </div>
            )}
            {isApproveCommentsAllowed && formApproveComments && (
              <div className="form-approve-message-container">
                <Space align="start">
                  <Text className="approve-icon">
                    <CheckCircleOutlined />
                  </Text>
                  <Text className="form-approve-message">
                    Approval comment: {formApproveComments}
                  </Text>
                </Space>
              </div>
            )}
            <div className="form-renderer-back-button flex-wrap">
              {!isFormRenderedOnModal && <BackButton />}
              {!isFormRenderedOnModal && <p>{formName}</p>}
              {additionalFormDetails?.id && isDataAnalyzerAllowed && (
                <Tooltip title="Data Analyser" placement="top">
                  <div className="ms-2">
                    <SmartIcon
                      iconKey={"analyzeData"}
                      handleIconClick={handleAnalyzeFormData}
                      height={32}
                      width={32}
                    />
                  </div>
                </Tooltip>
              )}
            </div>
            {additionalFormDetails?.id && (
              <div className="d-flex justify-content-between align-items-center flex-wrap my-3">
                <div>
                  {role === "Volunteer Coordinator" && (
                    <div>
                      By:{" "}
                      <span className="font-weight-bold">
                        {getFullName(
                          coordinator?.givenName ?? workforceDetails?.givenName,
                          coordinator?.familyName ??
                            workforceDetails?.familyName
                        )}
                      </span>
                    </div>
                  )}
                  {role === "Volunteer" && coordinator && (
                    <div>
                      By:{" "}
                      <span className="font-weight-bold">
                        {getFullName(
                          coordinator?.givenName,
                          coordinator?.familyName
                        )}
                      </span>
                    </div>
                  )}
                  <div>
                    On:{" "}
                    <span className="font-weight-bold">
                      {dateAndTime(additionalFormDetails?.createdTimestamp)}
                    </span>
                  </div>

                  <div>
                    Status:{" "}
                    <span className="font-weight-bold">
                      {additionalFormDetails?.status === "Submitted"
                        ? "Pending Approval"
                        : additionalFormDetails?.status}
                    </span>
                  </div>
                </div>
                {!formSummary &&
                  additionalFormDetails?.status !== "Approved" && (
                    <FormActionsButtons
                      setFormReadOnly={setFormReadOnly}
                      role={role}
                      additionalFormDetails={additionalFormDetails}
                      isApproveCommentsAllowed={isApproveCommentsAllowed}
                    />
                  )}
              </div>
            )}

            {isCountTowards5percent && (
              <p className="mt-4 form-submit-note">
                This form qualifies for 5% compliance reporting. The hours you
                log here count toward the 5% Requirement under CMS §418.70.
              </p>
            )}
            {formAttributes?.some(
              (attribute) =>
                attribute === "Volunteer Default" || attribute === "VC/PFA"
            ) && (
              <div className={isCountTowards5percent ? "" : "mt-4"}>
                {userDetails?.jobTitle === "Volunteer Coordinator" &&
                  (!workforceIdentifier ||
                    workforceIdentifier === "undefined" ||
                    coordinator) && (
                    <SelectWorkforce
                      workforceName={workforceName || workforceDetails}
                      setWorkforceName={setWorkforceName}
                      showCancel={(opportunities.length === 1 || additionalFormDetails?.status === "Approved") ? false : true}
                      workforceList={opportunities?.map(
                        (item) => item?.workforce
                      )}
                      label={"On the Volunteer’s Behalf"}
                    />
                  )}
                {patientDetails && (
                  <>
                    <span className="section-one-header d-inline-block mb-2">
                      Patient Name
                    </span>
                    <SelectedProfileCard
                      selectedItems={patientName}
                      showCancel={false}
                    />
                  </>
                )}
              </div>
            )}
            {formAttributes?.includes("Date Required") && (
              <div className="my-3">
                <CalenderAndDate
                  onDateChange={onDateChange}
                  label="Date Service Performed"
                  readOnly={formReadOnly}
                  date={taskDate}
                  required={formAttributes?.includes("Date Required")}
                />
              </div>
            )}
            {formAttributes?.includes("Time Required") && (
              <>
                <div className="my-3">
                  Time Spent:{" "}
                  <b>
                    {getTimeSpentFromTimestamps(startTime, endTime) || "0h 0m"}
                  </b>
                </div>
                <div className="time-selection-container">
                  <div className="time-selection">
                    <span className="required-mark-label">Start Time</span>
                    <TimePicker
                      disabled={formReadOnly}
                      value={startTime ? moment(startTime) : null}
                      placeholder="hh:mm"
                      format="hh:mm A"
                      minuteStep={15}
                      style={{ width: "100%" }}
                      onChange={handleSetStartTime}
                      onSelect={handleSetStartTime}
                      popupClassName="time-picker-custom-class"
                      allowClear={false}
                      use12Hours={true}
                    />
                  </div>
                  <div className="time-selection">
                    <span className="required-mark-label">End Time</span>
                    <TimePicker
                      disabled={formReadOnly}
                      value={endTime ? moment(endTime) : null}
                      placeholder="hh:mm"
                      format="hh:mm A"
                      minuteStep={15}
                      style={{ width: "100%" }}
                      onChange={handleSetEndTime}
                      onSelect={handleSetEndTime}
                      popupClassName="time-picker-custom-class"
                      allowClear={false}
                      use12Hours={true}
                    />
                    <p
                      className={`info-text-normal${
                        timeValidationError ? " form-missing-fields-message" : ""
                      }`}
                    >
                      *{" "}
                      {timeValidationError ??
                        "For visits crossing midnight, the end time can be earlier than the start time"}
                    </p>
                  </div>
                </div>
              </>
            )}
            <div style={{ position: "relative" }}>
              <Form
                key={`form-${formReadOnly}`}
                form={dynamicSchema}
                onSubmit={(userResponse) =>
                  submitUserResponse(userResponse, "Submitted")
                }
                onChange={handleFormChange}
                submission={
                  Object.keys(filledFormData).length > 0
                    ? { data: filledFormData }
                    : undefined
                }
                options={{
                  readOnly:
                    formReadOnly ||
                    (isCoordinatorForm && role !== "Volunteer Coordinator"),
                }}
              />
              {!isCoordinatorForm && (
                <>
                  {formAttributes?.some(
                    (attribute) =>
                      attribute === "Volunteer Default" ||
                      attribute === "VC/PFA"
                  ) && (
                    <Space className={!formSummary ? "form-buttons" : "mb-2"}>
                      {Boolean(
                        taskId &&
                          taskId !== "undefined" &&
                          (userDetails?.jobTitle === "Volunteer Coordinator" ||
                            (userDetails?.jobTitle === "Volunteer" &&
                              additionalFormDetails?.status === "Filled")) &&
                          !formSummary
                      ) && (
                        <Button
                          type="primary"
                          className="form-submit-btn"
                          onClick={handleDeleteClick}
                          loading={deleteLoading}
                        >
                          Delete
                        </Button>
                      )}
                      {Boolean(!formSummary) && (
                        <Popconfirm
                          title="This form will be saved but not submitted. Click Confirm to save or Submit to send it to your Volunteer Coordinator."
                          onConfirm={handleSaveClick}
                          okText="Confirm"
                          cancelText="Cancel"
                          placement="top"
                          overlayClassName="article-delete-popconfirm"
                          disabled={isSaveDisabled}
                        >
                          <Button
                            type="primary"
                            className="form-submit-btn"
                            disabled={isSaveDisabled}
                          >
                            Save
                          </Button>
                        </Popconfirm>
                      )}
                      {Boolean(!formSummary) && (
                        <Button
                          type="primary"
                          className="form-submit-btn"
                          onClick={() => {
                            if (window.history.length > 1) {
                              navigate(-1);
                            } else {
                              navigate(`${RoleToLinkMapper(role)}homepage`);
                            }
                          }}
                        >
                          Cancel
                        </Button>
                      )}
                      {Boolean(
                        userDetails?.jobTitle === "Volunteer Coordinator" &&
                          additionalFormDetails?.status === "Approved"
                      ) && (
                        <Button
                          type="primary"
                          className="form-submit-btn"
                          onClick={exportPDF}
                        >
                          Export
                        </Button>
                      )}
                    </Space>
                  )}
                </>
              )}
            </div>
            {missingRequiredFields.length > 0 &&
              !formSummary &&
              !formReadOnly && (
                <div className="form-missing-fields-message-container">
                  <Space align="start">
                    <Text className="form-missing-fields-message">
                      Please fill in the following required field
                      {missingRequiredFields.length > 1 ? "s" : ""} before
                      submitting:{" "}
                      <strong>{missingRequiredFields.join(", ")}</strong>
                    </Text>
                  </Space>
                </div>
              )}
            {formAttributes?.some(
              (attribute) =>
                attribute === "Volunteer Default" || attribute === "VC/PFA"
            ) && (
              <p className="form-submit-note">
                <strong>PLEASE NOTE:</strong> By clicking the SUBMIT button you
                are attesting that information you entered on the form is true,
                accurate and complete to the best of your knowledge and you
                understand that any falsification, omission, or concealment of
                material fact may subject you to adininistrative, civil, or
                criminal liability.
              </p>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default FormRenderer;
